import { withoutForbiddenXmlCharacters } from '@eformulieren/model'
import type { ReactNode } from 'react'
import { Counter } from '../Counter.js'
import { type SingleProps, type TextProps, asText } from './kinds.js'
import { useTextBinding } from './textBinding.js'
import { RefusedCharactersNotice, useRefusedCharacters } from './refusedCharacters.js'

/**
 * Eén regel antwoord: `text`, `url`, `email` en `phone`.
 *
 * Vier controltypes en één component, want ze verschillen alleen in het
 * toetsenbord dat een telefoon opent en in wat de browser aanbiedt. De
 * formaatcontrole is geen zaak van de control maar een regelsoort
 * (`identifierFormaat`, R-27): het invoertype `email` van de browser meldt in zijn
 * eigen taal en zijn eigen moment, en dat is precies wat R-30 en R-25 anders
 * regelen.
 *
 * **Geen `maxlength` bij een tekenlimiet.** De grens is een regel en de teller
 * hoort te kunnen laten zien dat hij is overschreden (R-12, R-13). Met `maxlength`
 * kapt de browser stil af, ziet de invuller nooit een melding en verliest hij bij
 * plakken het staartje van zijn tekst zonder dat iemand het zegt.
 *
 * **`text` is gebonden aan een `Y.Text`, de andere drie niet.** In het formulier krijgt `text` zijn
 * tekst uit het document (`vorm: 'tekst'`) en voegen twee medeschrijvers per teken samen (R-72,
 * `useTextBinding`). `url`, `email` en `phone` blijven één waarde (`vorm: 'waarde'`): korte velden
 * die zelden door twee mensen tegelijk worden bewerkt, en een `input type="email"` kent geen
 * cursorpositie die de binding zou kunnen rechtzetten. Wie een tekstregel zonder document tekent
 * (een test, een los voorbeeld) kan hem nog steeds een waarde geven.
 */

const TYPE = {
  text: 'text',
  url: 'url',
  email: 'email',
  phone: 'tel',
} as const

export type TextLineKind = keyof typeof TYPE

export type TextLineProps = (SingleProps | TextProps) & {
  readonly soort: TextLineKind
}

export function TextLine(props: TextLineProps): ReactNode {
  return props.vorm === 'tekst' ? <BoundTextLine {...props} /> : <ValueTextLine {...props} />
}

function describedBy(link: SingleProps['koppeling'], counterId: string | undefined): string | undefined {
  const description = [link['aria-describedby'], counterId]
    .filter((part): part is string => part !== undefined)
    .join(' ')
  return description === '' ? undefined : description
}

/** Een tekstregel aan een `Y.Text`: de tekst komt uit het document en gaat er per teken in terug. */
function BoundTextLine({
  soort: kind,
  taal: language,
  koppeling: link,
  tekst: text,
  onOwnChange,
  bewerkbaar: editable,
  limieten: limits,
}: TextProps & { readonly soort: TextLineKind }): ReactNode {
  const { refused, report } = useRefusedCharacters()
  const { ref, current } = useTextBinding<HTMLInputElement>(text, { editable, onOwnChange, onFiltered: report })
  const counterId = `${link.id}-teller`
  return (
    <>
      <input
        ref={ref}
        className="ef-invoer"
        type={TYPE[kind]}
        {...link}
        aria-describedby={describedBy(link, limits === undefined ? undefined : counterId)}
        defaultValue={current}
        readOnly={!editable}
      />
      <Counter tekst={current} limits={limits} taal={language} id={counterId} />
      <RefusedCharactersNotice refused={refused} taal={language} />
    </>
  )
}

function ValueTextLine({
  soort: kind,
  taal: language,
  koppeling: link,
  waarde: value,
  onWaarde: onValue,
  bewerkbaar: editable,
  limieten: limits,
}: SingleProps & { readonly soort: TextLineKind }): ReactNode {
  const textValue = asText(value)
  const { refused, report } = useRefusedCharacters()
  const counterId = `${link.id}-teller`
  const description = [link['aria-describedby'], limits === undefined ? undefined : counterId]
    .filter((part): part is string => part !== undefined)
    .join(' ')

  return (
    <>
      <input
        className="ef-invoer"
        type={TYPE[kind]}
        {...link}
        aria-describedby={description === '' ? undefined : description}
        value={textValue}
        readOnly={!editable}
        onChange={(event) => {
          // Zonder de tekens die niet kunnen worden verstuurd, zoals bij de binding (`textBinding.ts`).
          const typed = event.target.value
          const createNew = withoutForbiddenXmlCharacters(typed)
          report(createNew !== typed)
          // Leeg is leeg: een lege regel is geen antwoord met een lege tekst, want
          // dan zou `verplicht` erop slagen (R-26, engine `isLeeg`).
          onValue(createNew === '' ? undefined : createNew)
        }}
      />
      <Counter tekst={textValue} limits={limits} taal={language} id={counterId} />
      <RefusedCharactersNotice refused={refused} taal={language} />
    </>
  )
}
